
import { useState, useEffect } from "react";
import { ClientTherapistRelationship } from "@/types/therapist";
import { supabase } from "@/integrations/supabase/client";

interface ActivityTrackerResult {
  activeUsers: Record<string, boolean>;
  profileImages: Record<string, string>;
  activityPermissions: Record<string, boolean>;
  getClientActivityStatus: (relationship: ClientTherapistRelationship) => {
    status: "active" | "recent" | "inactive" | "unknown";
    text: string;
  };
  isClientSharing: Record<string, boolean>;
}

export const useClientActivityTracker = (clients: ClientTherapistRelationship[] | undefined): ActivityTrackerResult => {
  const [activeUsers, setActiveUsers] = useState<Record<string, boolean>>({});
  const [profileImages, setProfileImages] = useState<Record<string, string>>({});
  const [activityPermissions, setActivityPermissions] = useState<Record<string, boolean>>({});
  const [isClientSharing, setIsClientSharing] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    if (!clients || clients.length === 0) return;
    
    const fetchClientData = async () => {
      try {
        const clientIds = clients.map(c => c.client_id);
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, profile_image_url, show_activity')
          .in('user_id', clientIds);
          
        if (error) {
          console.error("Error fetching client profiles:", error);
          return;
        }
        
        const imageMap: Record<string, string> = {};
        const permissionMap: Record<string, boolean> = {};
        const sharingMap: Record<string, boolean> = {};
        
        data?.forEach(profile => {
          if (profile.profile_image_url) {
            imageMap[profile.user_id] = profile.profile_image_url;
          }
          permissionMap[profile.user_id] = profile.show_activity || false;
          sharingMap[profile.user_id] = profile.show_activity || false;
        });
        
        setProfileImages(imageMap);
        setActivityPermissions(permissionMap);
        setIsClientSharing(sharingMap);
      } catch (err) {
        console.error("Failed to fetch client data:", err);
      }
    };
    
    fetchClientData();
    
    const randomActivity = () => {
      if (!clients) return;
      
      const newActiveUsers: Record<string, boolean> = {};
      clients.forEach(client => {
        newActiveUsers[client.client_id] = Math.random() > 0.7;
      });
      setActiveUsers(newActiveUsers);
    };
    
    randomActivity();
    
    return () => {
      // Cleanup if needed
    };
  }, [clients]);

  const getClientActivityStatus = (relationship: ClientTherapistRelationship) => {
    const canSeeActivity = activityPermissions[relationship.client_id] || false;
    
    if (!canSeeActivity) {
      return { status: "unknown" as const, text: "Activity visibility disabled by client" };
    }
    
    if (activeUsers[relationship.client_id]) {
      return { status: "active" as const, text: "Currently active" };
    }
    
    if (!relationship.last_activity) {
      return { status: "inactive" as const, text: "No recent activity" };
    }
    
    const lastActive = new Date(relationship.last_activity);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) {
      return { status: "recent" as const, text: "Active today" };
    } else if (diffDays < 7) {
      return { status: "recent" as const, text: `Active ${diffDays} day${diffDays > 1 ? 's' : ''} ago` };
    } else {
      return { status: "inactive" as const, text: `Last active ${diffDays} days ago` };
    }
  };

  return { 
    activeUsers, 
    profileImages, 
    activityPermissions, 
    getClientActivityStatus,
    isClientSharing 
  };
};
